import type { CSSProperties, FC, ReactNode } from 'react'
import type { TileCell } from '@/components/landing/tiles'

import Image from 'next/image'
import { Fragment, useRef } from 'react'
import { Birds } from '@/components/landing/birds'
import { GlassTiles } from '@/components/landing/tiles'
import { useScrollProgress } from '@/hooks/use-scroll-progress'
import { art } from '@/constants/art'

const LEFT_TILES: TileCell[] = [[1, 0], [3, 0], [0, 1], [2, 1], [3, 1], [1, 2]]
const RIGHT_TILES: TileCell[] = [[1, 0], [1, 1], [0, 2], [2, 2], [1, 3], [3, 3], [0, 4]]
const LAKE_TILES: TileCell[] = [[0, 0], [2, 0], [1, 1], [2, 2]]

const HEADLINE_START = 350
const WORD_STEP = 80

interface WordsProps {
    text: string
    offset: number
}

// Each word blurs and rises into place in reading order.
const Words: FC<WordsProps> = ({ text, offset }): ReactNode => {
    return text.split(' ').map((word, index, words) => (
        <Fragment key={`${word}-${index}`}>
            <span
                className='inline-block motion-safe:animate-intro-words'
                style={{ animationDelay: `${HEADLINE_START + (offset + index) * WORD_STEP}ms` }}
            >
                {word}
            </span>
            {index < words.length - 1 && ' '}
        </Fragment>
    ))
}

const parallax = (x: string, y: string): CSSProperties => ({
    translate: `calc(var(--hero-progress, 0) * ${x}) calc(var(--hero-progress, 0) * ${y})`
})

export const HeroSection: FC = (): ReactNode => {
    const sectionRef = useRef<HTMLElement>(null)
    const { cloud, heroLandscape } = art

    useScrollProgress(sectionRef, '--hero-progress')

    return (
        <section
            ref={sectionRef}
            id='hero'
            aria-labelledby='hero-title'
            className='relative isolate flex min-h-[max(100svh,680px)] flex-col overflow-hidden bg-white'
        >
            {cloud && (
                <>
                    <div
                        aria-hidden
                        className='pointer-events-none absolute top-[3%] -left-[5%] z-0 w-[clamp(220px,38vw,580px)] mix-blend-multiply motion-safe:animate-intro-left'
                        style={{ ...parallax('-90px', '50px'), animationDelay: '150ms' }}
                    >
                        <div className='[--drift-x:70px] [--drift-y:-10px] motion-safe:animate-drift' style={{ animationDuration: '16s' }}>
                            <Image
                                src={cloud.src}
                                alt=''
                                width={cloud.width}
                                height={cloud.height}
                                priority
                                sizes='(min-width: 1536px) 580px, 38vw'
                                className='h-auto w-full'
                            />
                        </div>
                    </div>

                    <div
                        aria-hidden
                        className='pointer-events-none absolute top-[7%] -right-[6%] z-0 w-[clamp(200px,33vw,520px)] mix-blend-multiply motion-safe:animate-intro-right'
                        style={{ ...parallax('90px', '70px'), animationDelay: '300ms' }}
                    >
                        <div className='[--drift-x:-70px] [--drift-y:8px] motion-safe:animate-drift' style={{ animationDelay: '-8s', animationDuration: '19s' }}>
                            <Image
                                src={cloud.src}
                                alt=''
                                width={cloud.width}
                                height={cloud.height}
                                priority
                                sizes='(min-width: 1536px) 520px, 33vw'
                                className='h-auto w-full -scale-x-100'
                            />
                        </div>
                    </div>
                </>
            )}

            <Birds className='z-0' />

            {heroLandscape && (
                <div
                    aria-hidden
                    className='pointer-events-none absolute inset-x-0 bottom-0 z-0 h-[66%] min-h-[360px]'
                    style={parallax('0px', '110px')}
                >
                    <div className='absolute inset-0 motion-safe:animate-intro-settle'>
                        <Image
                            src={heroLandscape.src}
                            alt=''
                            fill
                            priority
                            sizes='100vw'
                            className='fade-top object-cover object-[50%_72%] [--fade:14%]'
                        />

                        <GlassTiles cells={LEFT_TILES} size='clamp(38px, 5.5vw, 80px)' introDelay={1100} className='bottom-[12%] -left-[1.6%]' />
                        <GlassTiles cells={RIGHT_TILES} size='clamp(42px, 6.4vw, 96px)' introDelay={1250} className='-right-[5%] -bottom-[4%]' />
                        <GlassTiles cells={LAKE_TILES} size='clamp(24px, 3.1vw, 46px)' introDelay={1600} className='bottom-[16%] left-[41%] max-sm:hidden' />
                    </div>
                </div>
            )}

            <div
                className='relative z-10 mx-auto flex w-full max-w-5xl flex-1 flex-col items-center px-5 pt-[clamp(8.5rem,31svh,17rem)] pb-[46svh] text-center'
                style={{
                    opacity: 'calc(1 - var(--hero-progress, 0) * 1.4)',
                    translate: '0 calc(var(--hero-progress, 0) * -70px)'
                }}
            >
                <h1
                    id='hero-title'
                    className='font-heading text-[clamp(2.5rem,4.6vw,4.2rem)] leading-[1.14] font-normal tracking-[-0.03em] text-balance text-ink'
                >
                    <Words text='Hand off the busywork' offset={0} />
                    {' '}
                    <br className='max-md:hidden' />
                    <Words text='to' offset={4} />
                    {' '}
                    <em className='italic text-moss'>
                        <Words text='AI agents' offset={5} />
                    </em>
                    {' '}
                    <Words text='you can trust.' offset={7} />
                </h1>
            </div>
        </section>
    )
}
